import {
    File,
    Folder as FolderIcon,
    FileText,
    FileSpreadsheet,
    Presentation,
    Image as ImageIcon,
    Globe,
    Mail,
    Database,
    Box as BoxIcon,
} from "lucide-react"

import { C } from "@/theme/newDesignTokens"
import { DatabaseConnectorTypes } from "@/pages/connectors/connector-types"

export const sheetTypes = ["xls", "xlsx", "ods", "csv"]
export const presentationTypes = ["ppt", "pptx", "odp"]
export const textTypes = ["txt", "doc", "docx", "rtf", "md", "epub", "one", "odt"]
export const imageTypes = ["jpg", "jpeg", "png", "gif", "bmp", "tiff", "tif", "webp"]
export const htmlTypes = ["html", "htm", "xhtml", "mhtml", "xml"]
export const emailTypes = ["email"]
export const ifcTypes = ["ifc"]
export const dbTableTypes = ["sql"]

const DEFAULT_ICON_SIZE = 18
const DEFAULT_ICON_STROKE = 1.6

interface IconOptions {
    size?: number
    strokeWidth?: number
}

export const getIcon = (
    name: string,
    isFolder: boolean,
    row: any,
    options: IconOptions = {},
): JSX.Element => {
    const size = options.size ?? DEFAULT_ICON_SIZE
    const strokeWidth = options.strokeWidth ?? DEFAULT_ICON_STROKE
    const normalized = processFileName(name).toLowerCase().replace(" (in progress)", "")
    const ext =
        getDisplayExtension(name, row?.uniq_source_name) ||
        normalized.split(".").pop()?.toLowerCase() ||
        ""
    const iconProps = { size, strokeWidth, color: C.medGray }

    if (isFolder) {
        if (ext && sheetTypes.includes(ext)) return <FileSpreadsheet {...iconProps} />
        if (ext && emailTypes.includes(ext)) return <Mail {...iconProps} />
        if (DatabaseConnectorTypes.includes(row?.file_source?.toLowerCase()))
            return <Database {...iconProps} />
        return <FolderIcon {...iconProps} />
    }

    if (!normalized.includes(".") && row?.file_folder) {
        const folderExt = row.file_folder.split(".").pop()?.toLowerCase()
        if (folderExt && sheetTypes.includes(folderExt)) return <FileSpreadsheet {...iconProps} />
    }

    if (ext === "pdf") return <FileText {...iconProps} color={C.coral} />
    if (sheetTypes.includes(ext ?? "")) return <FileSpreadsheet {...iconProps} color={C.teal} />
    if (presentationTypes.includes(ext ?? "")) return <Presentation {...iconProps} />
    if (textTypes.includes(ext ?? "")) return <FileText {...iconProps} />
    if (imageTypes.includes(ext ?? "")) return <ImageIcon {...iconProps} />
    if (htmlTypes.includes(ext ?? "")) return <Globe {...iconProps} />
    if (emailTypes.includes(ext ?? "")) return <Mail {...iconProps} />
    if (dbTableTypes.includes(ext ?? "")) return <Database {...iconProps} />
    if (ifcTypes.includes(ext ?? "")) return <BoxIcon {...iconProps} />
    return <File {...iconProps} />
}

export const PDF_WRAPPED_EXT_PATTERN =
    /\.(docx|doc|xlsx|xls|pptx|ppt|odt|ods|odp|txt|rtf|csv|xml|mhtml|xhtml|htm|html|pages|key|numbers|epub|md|email|jpg|jpeg|png|gif|bmp|tiff|tif|webp|one|sql|ifc)\.pdf$/i

export const processFileName = (fileName: string): string => {
    fileName = fileName.replace(".tab", "")
    const res = PDF_WRAPPED_EXT_PATTERN.test(fileName) ? fileName.slice(0, -4) : fileName
    return res || ""
}

const stripUniquePrefix = (name: string): string => (name.length > 6 && name[5] === "_" ? name.slice(6) : name)

export interface FileNameSource {
    file_name?: string | null
    uniq_source_name?: string | null
    file_source?: string | null
}

const DB_FILE_SOURCES = ["mysql", "postgresql", "oracledb"] as const

/**
 * User-visible filename: unwrap .ext.pdf blobs and fall back to uniq_source_name
 * when legacy rows were stored as plain .pdf after Office conversion.
 */
export const resolveDisplayFileName = (
    fileName: string | null | undefined,
    uniqSourceName?: string | null
): string => {
    const cleaned = processFileName(fileName ?? "")
    const isPlainPdf =
        cleaned.toLowerCase().endsWith(".pdf") && !PDF_WRAPPED_EXT_PATTERN.test(fileName ?? "")

    if (isPlainPdf && uniqSourceName) {
        const fromSource = processFileName(stripUniquePrefix(uniqSourceName))
        if (fromSource && !fromSource.toLowerCase().endsWith(".pdf")) {
            return fromSource
        }
    }
    return cleaned
}

export const getDisplayExtension = (fileName: string | null | undefined, uniqSourceName?: string | null): string => {
    const display = resolveDisplayFileName(fileName, uniqSourceName)
    const dot = display.lastIndexOf(".")
    if (dot <= 0 || dot === display.length - 1) return ""
    return display.slice(dot + 1).toLowerCase()
}

/** Sidebar / metadata type badge label derived from the user-visible file format. */
export const getFileTypeKey = (
    fileName: string | null | undefined,
    uniqSourceName?: string | null
): string => {
    const ext = getDisplayExtension(fileName, uniqSourceName)
    if (!ext) return "DOC"
    if (ext === "pdf") return "PDF"
    if (sheetTypes.includes(ext)) return "Excel"
    if (presentationTypes.includes(ext)) return "PPT"
    if (ifcTypes.includes(ext)) return "BIM"
    if (dbTableTypes.includes(ext)) return "SQL"
    return "DOC"
}

/**
 * Filename for document/project lists — unwraps converted blobs and applies
 * the same DB / email / SQL display rules as the Knowledge page.
 */
export const resolveListDisplayFileName = (file: FileNameSource): string => {
    const name = resolveDisplayFileName(file.file_name, file.uniq_source_name)
    const source = (file.file_source || "").toLowerCase()
    if (DB_FILE_SOURCES.includes(source as (typeof DB_FILE_SOURCES)[number])) {
        return name.replace(".pdf", "")
    }
    return name.replace(".email", "").replace(".sql", "")
}

export const safeDecodeUriComponent = (value: string): string => {
    try {
        return decodeURIComponent(value)
    } catch {
        return value
    }
}

/** Page count is only meaningful for PDF previews, not converted Office/media blobs. */
export const shouldShowPageCount = (file: FileNameSource, numberOfPages?: number): boolean => {
    if (!numberOfPages) return false
    return getDisplayExtension(file.file_name, file.uniq_source_name) === "pdf"
}
